export type ModuleName =
  | 'SEO Manager'
  | 'GMB Manager'
  | 'Social Media Manager'
  | 'Website Builder'
  | 'Profile'
  | 'Dashboard'
  // | 'Settings'
  | 'Authentication'
  // | 'Billing'
  | 'Reporting'
  | 'AI Tools';

export interface TelemetryDestination {
  moduleName: ModuleName;
  submoduleName: string;
}

export interface TelemetryRouteDefinition extends TelemetryDestination {
  path: string;
  children?: readonly string[];
}

export const DASHBOARD_SECTIONS: Record<string, string> = {
  'strategy-dashboard': 'Strategy Overview',
  'keyword-planner': 'Keyword Planner',
  'as-is-state': 'AS-IS State',
  'goal-setting': 'Goal Setting',
  'action-plan': 'Action Plan',
  'implementation-overview': 'Implementation Overview',
  blog: 'Blog Generator',
  faq: 'FAQ Generator',
  'governance-overview': 'Governance Overview',
  'as-is-performance': 'AS-IS Performance',
  'goals-tracking': 'Goal Tracking',
  'action-plan-tracking': 'Action Plan Tracking',
  'competitor-benchmark': 'Competitor Benchmark',
  'optimization-overview': 'Optimization Overview',
  'ecosystem-monitor': 'Ecosystem Monitor',
  'foresight-recommends': 'Foresight Recommends',
  'insight-navigator': 'Insight Navigator',
  alerts: 'Alerts',
};

export const PROFILE_TABS: Record<string, string> = {
  contact: 'Contact & Identity',
  business: 'Business Details',
  social: 'Social Media',
  subscription: 'Subscription & Billing',
  help: 'Help & Support',
};

export const WEBSITE_BUILDER_STEPS: Record<number, string> = {
  1: 'Sitemap',
  2: 'Content',
  3: 'Design & Themes',
  4: 'Publish & Download',
};

export const SEO_AS_IS_TABS: Record<string, string> = {
  onpage: 'On-Page SEO',
  offpage: 'Off-Page SEO',
  technical: 'Core Web Vitals & Technical SEO',
};

export const DASHBOARD_COMPONENT_TABS: Record<string, readonly string[]> = {
  'keyword-planner': ['SEO', 'GMB'],
  'as-is-state': Object.values(SEO_AS_IS_TABS),
  'action-plan': Object.values(SEO_AS_IS_TABS),
  blog: ['Discovery', 'Generated Blogs'],
  faq: ['Generate', 'Saved FAQs'],
  'as-is-performance': Object.values(SEO_AS_IS_TABS),
  'action-plan-tracking': Object.values(SEO_AS_IS_TABS),
  'insight-navigator': ['On-Page SEO', 'Off-Page SEO', 'Core Web Vitals'],
  'tracking-reporting': ['Overview', 'Rankings', 'Traffic'],
};

export const CONTENT_MANAGER_ROUTES: Record<string, string> = {
  '/content-manager': 'Strategy',
  '/content-manager/': 'Strategy',
  '/content-manager/implementation': 'Implementation',
  '/content-manager/governance': 'Governance',
  '/content-manager/optimization': 'Optimization',
};

export const CONTENT_MANAGER_TABS: Record<string, readonly string[]> = {
  Strategy: ['YouTube', 'Facebook', 'Instagram', 'LinkedIn'],
  Implementation: ['YouTube', 'Facebook', 'Instagram', 'LinkedIn'],
  Governance: ['YouTube', 'Facebook', 'Instagram', 'LinkedIn'],
  Optimization: ['YouTube', 'Facebook', 'Instagram', 'LinkedIn'],
};

export const WEBSITE_BUILDER_TABS: Record<string, readonly string[]> = {
  Content: ['Home', 'About', 'Services', 'Products', 'Blog', 'Gallery', 'Contact'],
  Design: ['All', 'General', 'Restaurant', 'Salon', 'Clinic', 'Retail', 'Professional'],
};

export const TELEMETRY_MODULE_REGISTRY: readonly TelemetryRouteDefinition[] = [
  { path: '/', moduleName: 'Dashboard', submoduleName: 'Home' },
  {
    path: '/dashboard',
    moduleName: 'SEO Manager',
    submoduleName: DASHBOARD_SECTIONS['strategy-dashboard'],
    children: Object.values(DASHBOARD_SECTIONS),
  },
  {
    path: '/as-is',
    moduleName: 'SEO Manager',
    submoduleName: 'AS-IS State',
    children: Object.values(SEO_AS_IS_TABS),
  },
  { path: '/marketing-intricacies', moduleName: 'Dashboard', submoduleName: 'Marketing Workspaces' },
  { path: '/agency-dashboard', moduleName: 'Dashboard', submoduleName: 'Agency Dashboard' },
  {
    path: '/content-manager/*',
    moduleName: 'Social Media Manager',
    submoduleName: 'Strategy',
    children: Object.values(CONTENT_MANAGER_ROUTES),
  },
  {
    path: '/website-builder',
    moduleName: 'Website Builder',
    submoduleName: WEBSITE_BUILDER_STEPS[1],
    children: Object.values(WEBSITE_BUILDER_STEPS),
  },
  {
    path: '/profile',
    moduleName: 'Profile',
    submoduleName: PROFILE_TABS.contact,
    children: Object.values(PROFILE_TABS),
  },
  { path: '/gmb-manager', moduleName: 'GMB Manager', submoduleName: 'Overview' },
  { path: '/utility-manager', moduleName: 'SEO Manager', submoduleName: 'Utility Manager' },
  { path: '/analytics', moduleName: 'Reporting', submoduleName: 'Overview' },
  { path: '/reporting', moduleName: 'Reporting', submoduleName: 'Brand Reports' },
  // { path: '/settings', moduleName: 'Settings', submoduleName: 'Settings' },
  // { path: '/billing', moduleName: 'Billing', submoduleName: 'Subscription & Billing' },
  { path: '/login', moduleName: 'Authentication', submoduleName: 'Login' },
  { path: '/onboarding', moduleName: 'Authentication', submoduleName: 'Onboarding' },
  { path: '/onboarding/success', moduleName: 'Authentication', submoduleName: 'Onboarding Success' },
  { path: '/brand-onboarding', moduleName: 'Authentication', submoduleName: 'Brand Onboarding' },
  { path: '/support/tickets', moduleName: 'Profile', submoduleName: 'Help & Support' },
  { path: '/support/tickets/*', moduleName: 'Profile', submoduleName: 'Help & Support' },
] as const;

export function resolveTelemetryDestination(pathname: string, search = '', hash = ''): TelemetryDestination {
  let cleanPath = pathname;
  const base = import.meta.env.BASE_URL || '/';
  if (base !== '/' && cleanPath.startsWith(base)) {
    cleanPath = cleanPath.substring(base.length);
  }
  if (!cleanPath.startsWith('/')) {
    cleanPath = '/' + cleanPath;
  }
  const normalizedPath = cleanPath !== '/' ? cleanPath.replace(/\/+$/, '') : cleanPath;

  if (normalizedPath === '/dashboard') {
    const section = hash.replace(/^#/, '');
    return {
      moduleName: 'SEO Manager',
      submoduleName: DASHBOARD_SECTIONS[section] || DASHBOARD_SECTIONS['strategy-dashboard'],
    };
  }

  if (normalizedPath === '/profile') {
    const tab = new URLSearchParams(search).get('tab') || 'contact';
    return { moduleName: 'Profile', submoduleName: PROFILE_TABS[tab] || PROFILE_TABS.contact };
  }

  if (normalizedPath.startsWith('/content-manager')) {
    return {
      moduleName: 'Social Media Manager',
      submoduleName: CONTENT_MANAGER_ROUTES[normalizedPath] || 'Unknown Content Page',
    };
  }

  const route = TELEMETRY_MODULE_REGISTRY.find(({ path }) => {
    const prefix = path.replace('/*', '');
    return normalizedPath === prefix || (path.endsWith('/*') && normalizedPath.startsWith(prefix));
  });

  return route
    ? { moduleName: route.moduleName, submoduleName: route.submoduleName }
    : { moduleName: 'Dashboard', submoduleName: 'Unknown Page' };
}
